'use client';

import * as S from './styles.ts';
import DragAndDropAccordion from '@template/blocks/DragAndDropAccordion';
import { Input, Select, Switch } from '@template/components';
import { AutoField, Field } from '@measured/puck';
import { ArrayContainer } from '@template/blocks/DragAndDropAccordion/styles.ts';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import MarkdownEditor from '@admin/config/overrides/fieldTypes/MarkdownEditor';
import { useTypedSelector } from '@/redux/helpers/hooks.ts';
import ChevronDown from '@template/icons/ChevronDown.tsx';
import { IconSelect } from '@template/components/Select';
import { InputLabel } from '@template/components/Input/styles.ts';

type TCustomField = (Field<any> & { key: string })[];

export const MultiWrapper = ({ label, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <S.GroupWrapper>
      <S.GroupInputTrigger onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
        <InputLabel>{label}</InputLabel>
        <ChevronDown />
      </S.GroupInputTrigger>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{
              duration: 0.2,
              ease: 'easeOut',
            }}
            initial={{
              height: 0,
            }}
            animate={{
              height: 'auto',
            }}
            exit={{
              height: 0,
            }}
            style={{
              overflow: 'clip',
            }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </S.GroupWrapper>
  );
};

const fieldTypes = {
  image: ({ label, value, field, onChange }) => {
    const fields: TCustomField = [
      {
        key: 'src',
        label: 'Source',
        type: 'textarea',
      },
      {
        key: 'alt',
        label: 'Alt',
        type: 'textarea',
      },
    ];
    return (
      <MultiWrapper label={label}>
        <ArrayContainer>
          {fields.map(({ ...field }, i) => (
            <AutoField
              {...field}
              key={i}
              field={field}
              value={value?.[field.key]}
              onChange={(val) => {
                const newObj = {
                  ...(value || {}),
                  [field.key]: val,
                };
                onChange(newObj);
              }}
            />
          ))}
        </ArrayContainer>
      </MultiWrapper>
    );
  },
  button: ({ label, value, field, onChange }) => {
    const fields: TCustomField = [
      {
        key: 'children',
        label: 'Text',
        type: 'textarea',
      },
      {
        key: 'href',
        label: 'Link',
        type: 'text',
      },
      {
        key: 'variant',
        label: 'Variant',
        type: 'select',
        options: [
          { label: 'Flat', value: 'flat' },
          { label: 'Solid', value: 'solid' },
          { label: 'Outline', value: 'outline' },
          { label: 'Light', value: 'light' },
        ],
      },
      {
        key: 'size',
        label: 'Size',
        type: 'select',
        options: [
          { label: 'X Small', value: 'xs' },
          { label: 'Small', value: 'sm' },
          { label: 'Medium', value: 'md' },
          { label: 'Large', value: 'lg' },
        ],
      },
      {
        key: 'target',
        label: 'Open In New Tab',
        type: 'select',
        options: [
          { label: 'Yes', value: '_blank' },
          { label: 'No', value: '_self' },
        ],
      },
      {
        key: 'color',
        label: 'Color',
        type: 'select',
        options: [
          { label: 'Default', value: 'default' },
          { label: 'Primary', value: 'primary' },
          { label: 'Secondary', value: 'secondary' },
          { label: 'Success', value: 'success' },
        ],
      },
      {
        key: 'title',
        label: 'Title',
        type: 'textarea',
      },
    ];
    return (
      <MultiWrapper label={label}>
        <ArrayContainer>
          {fields.map(({ key, ...field }, i) => (
            <AutoField
              {...field}
              key={i}
              field={field}
              value={value?.[key]}
              onChange={(val) => {
                const newObj = {
                  ...(value || {}),
                  [key]: val,
                };
                onChange(newObj);
              }}
            />
          ))}
        </ArrayContainer>
      </MultiWrapper>
    );
  },
  input: ({ label, value, field, onChange }) => {
    const hasEditPermission = useTypedSelector(
      (state) => state.admin.permissions.edit
    );

    const fields: TCustomField = [
      {
        key: 'label',
        label: 'Label',
        type: 'textarea',
      },
      {
        key: 'placeholder',
        label: 'Placeholder',
        type: 'textarea',
      },
      {
        key: 'prefix',
        label: 'Prefix',
        type: 'textarea',
      },
      {
        key: 'suffix',
        label: 'Suffix',
        type: 'textarea',
      },
    ];
    return (
      <MultiWrapper label={label}>
        <ArrayContainer>
          {fields.map(({ key, ...field }, i) => (
            <AutoField
              {...field}
              key={i}
              field={field}
              value={value?.[key]}
              onChange={(val) => {
                const newObj = {
                  ...(value || {}),
                  [key]: val,
                };
                onChange(newObj);
              }}
            />
          ))}
          <S.Wrapper style={{ gap: 4 }}>
            <InputLabel>Hint</InputLabel>
            <MarkdownEditor
              value={value?.['hint']}
              onChange={(val) => {
                const newObj = {
                  ...(value || {}),
                  hint: val,
                };
                onChange(newObj);
              }}
              disabled={!hasEditPermission}
            />
          </S.Wrapper>
        </ArrayContainer>
      </MultiWrapper>
    );
  },
  boolean: ({
    label,
    value,
    field,
    name,
    onChange,
  }: {
    label: string;
    value: boolean;
    field: any;
    name?: string;
    onChange: any;
  }) => {
    const hasEditPermission = useTypedSelector(
      (state) => state.admin.permissions.edit
    );
    const { key, ...rest } = field;
    return (
      <S.Wrapper>
        <Switch
          checked={value}
          onChange={onChange}
          name={name}
          label={label}
          variant='flat'
          {...rest}
          disabled={field.disabled || !hasEditPermission}
        />
      </S.Wrapper>
    );
  },
  icon: ({ label, onChange, value, field }) => {
    const hasEditPermission = useTypedSelector(
      (state) => state.admin.permissions.edit
    );
    const { key, ...rest } = field;

    return (
      <S.Wrapper>
        <IconSelect
          {...rest}
          labelPosition='outside'
          selected={value || ''}
          label={label}
          onChange={onChange}
          disabled={field.disabled || !hasEditPermission}
        />
      </S.Wrapper>
    );
  },
  text: ({ label, name, onChange, value, field }) => {
    const hasEditPermission = useTypedSelector(
      (state) => state.admin.permissions.edit
    );
    const { key, ...rest } = field;

    return (
      <S.Wrapper>
        <Input
          {...rest}
          label={label}
          name={name}
          value={value}
          labelPosition='outside'
          onChange={onChange}
          type='text'
          disabled={field.disabled || !hasEditPermission}
        />
      </S.Wrapper>
    );
  },
  markdown: ({ label, value, onChange }) => {
    const hasEditPermission = useTypedSelector(
      (state) => state.admin.permissions.edit
    );

    return (
      <S.Wrapper style={{ gap: 4 }}>
        <InputLabel>{label}</InputLabel>
        <MarkdownEditor
          value={value}
          onChange={onChange}
          disabled={!hasEditPermission}
        />
      </S.Wrapper>
    );
  },
  number: ({ label, name, onChange, value, field }) => {
    const hasEditPermission = useTypedSelector(
      (state) => state.admin.permissions.edit
    );
    const { key, ...rest } = field;

    return (
      <S.Wrapper>
        <Input
          {...rest}
          label={label}
          name={name}
          value={value}
          labelPosition='outside'
          onChange={(val) => onChange(Number(val))}
          type='number'
          disabled={!hasEditPermission}
        />
      </S.Wrapper>
    );
  },
  textarea: ({ label, name, onChange, value, field }) => {
    const hasEditPermission = useTypedSelector(
      (state) => state.admin.permissions.edit
    );
    const { key, ...rest } = field;

    return (
      <S.Wrapper>
        <Input
          {...rest}
          type='textarea'
          labelPosition='outside'
          label={label}
          name={name}
          value={value}
          onChange={onChange}
          autoHeight
          disabled={field.disabled || !hasEditPermission}
        />
      </S.Wrapper>
    );
  },
  radio: ({ label, field, value, onChange }) => {
    const hasEditPermission = useTypedSelector(
      (state) => state.admin.permissions.edit
    );
    const { key, ...rest } = field;

    return (
      <S.SelectWrapper>
        <Select
          {...rest}
          type={undefined}
          label={label}
          selected={value}
          onChange={onChange}
          options={field.options.map((opt) => ({
            label: opt.label,
            key: opt.value,
          }))}
          disabled={field.disabled || !hasEditPermission}
        />
      </S.SelectWrapper>
    );
  },
  select: ({ label, field, value, onChange }) => {
    const hasEditPermission = useTypedSelector(
      (state) => state.admin.permissions.edit
    );
    const { key, ...rest } = field;

    return (
      <S.SelectWrapper>
        <Select
          {...rest}
          selected={value}
          label={label}
          labelPosition='outside'
          onChange={onChange}
          options={field.options?.map((opt) => ({
            label: opt.label,
            key: opt.value,
          }))}
          disabled={field.disabled || !hasEditPermission}
        />
      </S.SelectWrapper>
    );
  },
  object: ({ label, onChange, value, field }) => {
    const objFields = field.objectFields || [];
    const fields = Object.keys(objFields).map((key) => ({
      key,
      ...objFields[key],
    }));
    return (
      <MultiWrapper label={label}>
        <ArrayContainer>
          {fields.map((field, i) => (
            <AutoField
              {...field}
              key={i}
              field={field}
              value={value?.[field.key]}
              onChange={(val) => {
                const newObj = { ...value };
                newObj[field.key] = val;
                onChange(newObj);
              }}
            />
          ))}
        </ArrayContainer>
      </MultiWrapper>
    );
  },
  array: ({ label, field, onChange, value }) => {
    const fields = field.arrayFields;
    const itemLabel = field.item_label;

    return (
      <MultiWrapper label={`${label} (${value?.length || 0})`}>
        <DragAndDropAccordion
          items={value || []}
          fields={fields}
          onChange={onChange}
          itemLabel={itemLabel || 'Item'}
        />
      </MultiWrapper>
    );
  },
  custom: ({ render, ...props }) => render(props),
};

export default fieldTypes;
